import type { FirstStepsStateDto, FirstStepsTaskDto } from "../../../api/dto/reward_widgets.dto.js";
import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { LocalizationManager } from "../../../localization/localization_manager.js";
import { RewardWidgetsService } from "../../../reward_widgets/reward_widgets_service.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { FirstStepsChestOverlay, FirstStepsChestOverlayParams } from "./first_steps_chest_overlay.js";
import { AdventureTooltip } from "../../tooltip/adventure_tooltip.js";
import { TooltipManager } from "../../tooltip/tooltip_manager.js";
import { ADVENTURE_REWARD_ICONS } from "../../../adventure/adventure_box_rewards.js";
import { rewardAmountText } from "../../../common/reward_format.js";
import { Game } from "../../../game.js";
import { MirrorMode, UIMirrorElement } from "../../common_ui_elements/ui_mirror_element.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { UIProgressBar } from "../../common_ui_elements/ui_progress_bar.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement, UITextOptions } from "../../common_ui_elements/ui_text_element.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";

export interface FirstStepsOverlayParams
{
  state: FirstStepsStateDto;
  preview?: boolean;
  fresh_task_ids?: string[];
  on_go?: ( task: FirstStepsTaskDto ) => void;
  on_claimed?: () => void;
}

const previewTask = ( id: string, metric: string, current: number, target: number ): FirstStepsTaskDto =>
  ( { id, metric, param: null, title: null, current, target } );

export const FIRST_STEPS_PREVIEW_STATE: FirstStepsStateDto = {
  tasks: [
    previewTask( "levels", "reach_level", 25, 50 ),
    previewTask( "boosters", "use_boosters", 4, 10 ),
    previewTask( "coins", "spend_coins", 900, 5000 ),
    previewTask( "bot", "start_bot", 0, 1 ),
    previewTask( "wheel", "spin_wheel", 30, 30 ),
  ],
  rewards: [
    { type: "coin", amount: 2500 },
    { type: "big_balls", amount: 5 },
    { type: "infinite_balls", amount: 5 },
    { type: "multiballs", amount: 5 },
    { type: "fireworks", amount: 5 },
    { type: "infinite_hearts", amount: 15 },
  ],
  completed: 1,
  claimable: false,
  bot_url: null,
};

export const FIRST_STEPS_PREVIEW_DONE_STATE: FirstStepsStateDto = {
  ...FIRST_STEPS_PREVIEW_STATE,
  tasks: FIRST_STEPS_PREVIEW_STATE.tasks.map( task => ( { ...task, current: task.target } ) ),
  completed: FIRST_STEPS_PREVIEW_STATE.tasks.length,
  claimable: true,
};

export const FIRST_STEPS_PREVIEW_FRESH_IDS = [ "levels", "bot" ];

export const FIRST_STEPS_PREVIEW_FRESH_STATE: FirstStepsStateDto = {
  ...FIRST_STEPS_PREVIEW_STATE,
  tasks: FIRST_STEPS_PREVIEW_STATE.tasks.map( task => FIRST_STEPS_PREVIEW_FRESH_IDS.includes( task.id ) ? { ...task, current: task.target } : task ),
  completed: FIRST_STEPS_PREVIEW_STATE.completed + FIRST_STEPS_PREVIEW_FRESH_IDS.length,
};

interface BarSkin
{
  track_id: ImgID;
  track_key: string;
  fill_id: ImgID;
  fill_key: string;
  inset_x: number;
  inset_y: number;
  track_cap_ratio: number;
  fill_cap_ratio: number;
}

export class FirstStepsOverlay extends AbstractOverlay
{
  private static readonly DESIGN_W = 1080;
  private static readonly DESIGN_H = 1920;

  private static readonly CASTLE_X = 15;
  private static readonly CASTLE_Y = 60;
  private static readonly CASTLE_W = 1051;
  private static readonly CASTLE_H = 651;

  private static readonly LOGO_W = 430;
  private static readonly LOGO_H = 358;
  private static readonly LOGO_Y = 50;

  private static readonly CLOSE_SIZE = 130;
  private static readonly CLOSE_X = 868;
  private static readonly CLOSE_Y = 120;

  private static readonly SUBTITLE_X = 40;
  private static readonly SUBTITLE_Y = 392;
  private static readonly SUBTITLE_W = 1000;
  private static readonly SUBTITLE_H = 60;
  private static readonly SUBTITLE_FONT = 46;
  private static readonly SUBTITLE_OUTLINE = 9;

  private static readonly HEADER_X = 100;
  private static readonly HEADER_Y = 470;
  private static readonly HEADER_W = 935;
  private static readonly HEADER_H = 220;

  private static readonly PROGRESS_X = 135;
  private static readonly PROGRESS_Y = 63;
  private static readonly PROGRESS_W = 600;
  private static readonly PROGRESS_H = 86;
  private static readonly PROGRESS_INSET_X = 13;
  private static readonly PROGRESS_INSET_Y = 13;
  private static readonly PROGRESS_TRACK_CAP_RATIO = 62 / 86;
  private static readonly PROGRESS_FILL_CAP_RATIO = 54 / 64;
  private static readonly PROGRESS_FONT = 45;

  private static readonly KEY_X = 0;
  private static readonly KEY_Y = 18;
  private static readonly KEY_W = 200;
  private static readonly KEY_H = 201;

  private static readonly CHEST_X = 650;
  private static readonly CHEST_Y = -70;
  private static readonly CHEST_W = 306;
  private static readonly CHEST_H = 270;

  private static readonly PANEL_X = 11;
  private static readonly PANEL_Y = 688;
  private static readonly PANEL_W = 1058;
  private static readonly PANEL_H = 1077;

  private static readonly ROW_X = 51;
  private static readonly ROW_Y = 64;
  private static readonly ROW_W = 956;
  private static readonly ROW_H = 182;
  private static readonly ROW_STEP = 196;
  private static readonly ROW_SRC_H = 180;
  private static readonly ROW_SRC_LEFT_CAP = 200;
  private static readonly ROW_SRC_RIGHT_CAP = 60;

  private static readonly ICON_CENTER_X = 77;
  private static readonly ICON_CENTER_Y = 91;
  private static readonly ROW_KEY_SIZE = 115;
  private static readonly CHECK_W = 100;
  private static readonly CHECK_H = 95;

  private static readonly TITLE_X = 158;
  private static readonly TITLE_Y = 12;
  private static readonly TITLE_W = 780;
  private static readonly TITLE_H = 62;
  private static readonly TITLE_FONT = 40;
  private static readonly TITLE_MIN_FONT = 30;

  private static readonly BAR_X = 160;
  private static readonly BAR_Y = 96;
  private static readonly BAR_W = 403;
  private static readonly BAR_H = 60;
  private static readonly BAR_INSET = 3;
  private static readonly TASK_TRACK_CAP_RATIO = 49 / 60;
  private static readonly TASK_FILL_CAP_RATIO = 49 / 54;
  private static readonly BAR_FONT = 40;

  private static readonly BUTTON_X = 606;
  private static readonly BUTTON_Y = 80;
  private static readonly BUTTON_W = 340;
  private static readonly BUTTON_H = 93;
  private static readonly BUTTON_FONT = 42;

  private static readonly DONE_X = 172;
  private static readonly DONE_Y = 92;
  private static readonly DONE_W = 500;
  private static readonly DONE_H = 62;
  private static readonly DONE_FONT = 42;

  private static readonly TEXT_OUTLINE = 10;

  private static readonly CHEST_PULSE_SCALE = 1.08;
  private static readonly CHEST_PULSE_MS = 600;

  private static readonly SEEN_DONE_KEY = "first_steps_seen_done";
  private static readonly DONE_ANIM_DELAY_MS = 450;
  private static readonly DONE_ANIM_STEP_MS = 900;
  private static readonly ROW_SWAP_MS = 320;
  private static readonly ROW_POP_SCALE = 1.05;
  private static readonly ROW_MOVE_MS = 420;
  private static readonly KEY_FLY_MS = 620;
  private static readonly KEY_FLY_END_SCALE = 0.75;
  private static readonly PROGRESS_FILL_MS = 450;
  private static readonly HEADER_KEY_POP_SCALE = 1.15;
  private static readonly HEADER_KEY_POP_MS = 320;

  private static readonly TOOLTIP_ONE_ROW_MAX = 6;
  private static readonly TOOLTIP_SCREEN_MARGIN = 18;
  private static readonly TOOLTIP_TAIL_MARGIN = 70;
  private static readonly TOOLTIP_OVERLAP = 20;

  private static readonly PROGRESS_SKIN: BarSkin = {
    track_id: ImgID.UI_FirstStepsProgressTrack,
    track_key: "first_steps_progress_track",
    fill_id: ImgID.UI_FirstStepsProgressFill,
    fill_key: "first_steps_progress_fill",
    inset_x: FirstStepsOverlay.PROGRESS_INSET_X,
    inset_y: FirstStepsOverlay.PROGRESS_INSET_Y,
    track_cap_ratio: FirstStepsOverlay.PROGRESS_TRACK_CAP_RATIO,
    fill_cap_ratio: FirstStepsOverlay.PROGRESS_FILL_CAP_RATIO,
  };

  private static readonly TASK_SKIN: BarSkin = {
    track_id: ImgID.UI_FirstStepsTaskBarTrack,
    track_key: "first_steps_task_track",
    fill_id: ImgID.UI_FirstStepsTaskBarFill,
    fill_key: "first_steps_task_fill",
    inset_x: FirstStepsOverlay.BAR_INSET,
    inset_y: FirstStepsOverlay.BAR_INSET,
    track_cap_ratio: FirstStepsOverlay.TASK_TRACK_CAP_RATIO,
    fill_cap_ratio: FirstStepsOverlay.TASK_FILL_CAP_RATIO,
  };

  private readonly overlay_params: FirstStepsOverlayParams;
  private readonly content: UIElementHolder;
  private state: FirstStepsStateDto;
  private fresh_ids: Set<string>;
  private shown_done: number;
  private header?: UIElement;
  private panel?: UIElement;
  private progress_bar?: UIProgressBar;
  private progress_text?: UITextElement;
  private header_key?: UIImage;
  private fresh_order = 0;
  private build = 0;
  private task_rows = new Map<string, UIElement[]>();
  private placed_done = new Set<string>();

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, params: FirstStepsOverlayParams, overlay_id: OverlayId )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );

    this.overlay_params = params;
    this.state = params.state;
    this.fresh_ids = new Set( params.fresh_task_ids ?? ( params.preview ? [] : FirstStepsOverlay.unseenDoneTaskIds( params.state ) ) );
    this.shown_done = Math.max( 0, params.state.completed - this.fresh_ids.size );
    if ( !params.preview )
      FirstStepsOverlay.markDoneTasksSeen( params.state );

    const content_w = LayoutScaler.widthFromDesign( FirstStepsOverlay.DESIGN_W );
    const content_h = LayoutScaler.heightFromDesign( FirstStepsOverlay.DESIGN_H );
    const content_x = this.rect.getW2() - content_w / 2;
    const content_y = this.rect.getH2() - content_h / 2;

    this.content = new UIElementHolder( this.ctx, content_x, content_y, content_w, content_h );
    this.addChild( this.content );

    this.createCastle();
    this.createLogo();
    this.createSubtitle();
    this.createHeader();
    this.createPanel();
    this.createCloseButton();

    if ( !params.preview )
      void this.refreshState();
  }

  private async refreshState(): Promise<void>
  {
    await RewardWidgetsService.refresh();

    const fresh = RewardWidgetsService.firstSteps();
    if ( !fresh || !OverlayManager.isOverlayOpen( this.getId() ) )
      return;

    if ( JSON.stringify( fresh ) === JSON.stringify( this.state ) )
      return;

    this.applyState( fresh );
  }

  private applyState( state: FirstStepsStateDto ): void
  {
    if ( TooltipManager.isAnyTooltipOpened() )
      TooltipManager.closeTooltip();

    this.fresh_ids = new Set( FirstStepsOverlay.unseenDoneTaskIds( state ) );
    FirstStepsOverlay.markDoneTasksSeen( state );
    this.state = state;
    this.shown_done = Math.max( 0, state.completed - this.fresh_ids.size );
    this.fresh_order = 0;
    this.build++;

    if ( this.header )
      this.content.removeChild( this.header );
    if ( this.panel )
      this.content.removeChild( this.panel );

    this.createHeader();
    this.createPanel();
    this.forceMakeShouldBeRendered();
  }

  public static taskTitle( task: FirstStepsTaskDto ): string
  {
    const key = task.title ?? `FIRST_STEPS.TASK.${task.metric.toUpperCase()}`;
    const params = [ String( task.target ) ];
    if ( LocalizationManager.has( key ) )
      return LocalizationManager.tWithParams( key, params );

    return key.replace( /\{0\}/g, params[0] );
  }

  private static px( design: number ): number
  {
    return LayoutScaler.widthFromDesign( design );
  }

  private static seenDoneTaskIds(): string[] | null
  {
    try
    {
      const raw = localStorage.getItem( FirstStepsOverlay.SEEN_DONE_KEY );
      if ( raw === null )
        return null;

      const parsed = JSON.parse( raw );
      return Array.isArray( parsed ) ? parsed.filter( id => typeof id === "string" ) : [];
    }
    catch
    {
      return null;
    }
  }

  private static unseenDoneTaskIds( state: FirstStepsStateDto ): string[]
  {
    const seen = FirstStepsOverlay.seenDoneTaskIds();
    if ( seen === null )
      return [];

    return state.tasks.filter( task => task.current >= task.target && !seen.includes( task.id ) ).map( task => task.id );
  }

  private static markDoneTasksSeen( state: FirstStepsStateDto ): void
  {
    const done = state.tasks.filter( task => task.current >= task.target ).map( task => task.id );
    const seen = new Set( [ ...( FirstStepsOverlay.seenDoneTaskIds() ?? [] ), ...done ] );
    try
    {
      localStorage.setItem( FirstStepsOverlay.SEEN_DONE_KEY, JSON.stringify( [ ...seen ] ) );
    }
    catch
    {
    }
  }

  private addImage( parent: UIElement, id: ImgID, x: number, y: number, w: number, h: number ): UIImage
  {
    const image = new UIImage( this.ctx, x, y, w, h, id );
    image.setClickable( false );
    parent.addChild( image, true );
    return image;
  }

  private addNineSlice( parent: UIElement, id: ImgID, key: string, x: number, y: number, w: number, h: number, left_cap: number, right_cap: number, part_of_parent = true ): UINineSliceElement
  {
    const corner_size = {
      top_left: { width: left_cap, height: 0 },
      bottom_right: { width: right_cap, height: 0 },
    };

    const element = new UINineSliceElement( this.ctx, x, y, w, h, id, key, corner_size );
    element.setClickable( !part_of_parent );
    parent.addChild( element, part_of_parent );
    return element;
  }

  private addText( parent: UIElement, x: number, y: number, w: number, h: number, options: UITextOptions ): UITextElement
  {
    const text = new UITextElement( this.ctx, x, y, w, h, {
      font_weight: 900,
      text_style: TextStyleScheme.rubik,
      text_color: ColorScheme.white,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      shadow_offset_x: 0,
      shadow_offset_y: 0,
      auto_wrap: false,
      ...options,
    } );
    text.setClickable( false );
    parent.addChild( text, true );
    return text;
  }

  private createCastle(): void
  {
    const x = FirstStepsOverlay.px( FirstStepsOverlay.CASTLE_X );
    const y = FirstStepsOverlay.px( FirstStepsOverlay.CASTLE_Y );
    const w = FirstStepsOverlay.px( FirstStepsOverlay.CASTLE_W );
    const h = FirstStepsOverlay.px( FirstStepsOverlay.CASTLE_H );

    this.addImage( this.content, ImgID.UI_FirstStepsCastleBg, x, y, w, h );
  }

  private createLogo(): void
  {
    const w = FirstStepsOverlay.px( FirstStepsOverlay.LOGO_W );
    const h = FirstStepsOverlay.px( FirstStepsOverlay.LOGO_H );
    const x = ( this.content.getRect().getW() - w ) / 2;
    const y = FirstStepsOverlay.px( FirstStepsOverlay.LOGO_Y );

    this.addImage( this.content, ImgID.UI_FirstStepsLogo, x, y, w, h );
  }

  private createSubtitle(): void
  {
    const x = FirstStepsOverlay.px( FirstStepsOverlay.SUBTITLE_X );
    const y = FirstStepsOverlay.px( FirstStepsOverlay.SUBTITLE_Y );
    const w = FirstStepsOverlay.px( FirstStepsOverlay.SUBTITLE_W );
    const h = FirstStepsOverlay.px( FirstStepsOverlay.SUBTITLE_H );

    this.addText( this.content, x, y, w, h, {
      localization_key: "FIRST_STEPS.SUBTITLE",
      font_size: LayoutScaler.textSizeFromDesign( FirstStepsOverlay.SUBTITLE_FONT ),
      outline_color: ColorScheme.first_steps_subtitle_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( FirstStepsOverlay.SUBTITLE_OUTLINE ),
      color_map: { yellow: ColorScheme.first_steps_subtitle_highlight },
      scale_to_width: true,
    } );
  }

  private createHeader(): void
  {
    const state = this.state;
    const tasks = state.tasks;
    const done = this.shown_done;

    const x = FirstStepsOverlay.px( FirstStepsOverlay.HEADER_X );
    const y = FirstStepsOverlay.px( FirstStepsOverlay.HEADER_Y );
    const w = FirstStepsOverlay.px( FirstStepsOverlay.HEADER_W );
    const h = FirstStepsOverlay.px( FirstStepsOverlay.HEADER_H );

    const header = new UIElementHolder( this.ctx, x, y, w, h );
    this.content.addChild( header );
    this.header = header;

    const bar_x = FirstStepsOverlay.px( FirstStepsOverlay.PROGRESS_X );
    const bar_y = FirstStepsOverlay.px( FirstStepsOverlay.PROGRESS_Y );
    const bar_w = FirstStepsOverlay.px( FirstStepsOverlay.PROGRESS_W );
    const bar_h = FirstStepsOverlay.px( FirstStepsOverlay.PROGRESS_H );
    const bar = this.createBar( header, FirstStepsOverlay.PROGRESS_SKIN, bar_x, bar_y, bar_w, bar_h, done, tasks.length, false );
    this.progress_bar = bar;

    this.progress_text = this.addText( bar, 0, 0, bar_w, bar_h, {
      text: `${done}/${tasks.length}`,
      font_size: LayoutScaler.textSizeFromDesign( FirstStepsOverlay.PROGRESS_FONT ),
      outline_color: ColorScheme.black,
      outline_width: LayoutScaler.textBorderSizeFromDesign( FirstStepsOverlay.TEXT_OUTLINE ),
    } );

    const key_x = FirstStepsOverlay.px( FirstStepsOverlay.KEY_X );
    const key_y = FirstStepsOverlay.px( FirstStepsOverlay.KEY_Y );
    const key_w = FirstStepsOverlay.px( FirstStepsOverlay.KEY_W );
    const key_h = FirstStepsOverlay.px( FirstStepsOverlay.KEY_H );
    const header_key = new UIImage( this.ctx, key_x, key_y, key_w, key_h, ImgID.UI_FirstStepsKey );
    header_key.setClickable( false );
    header.addChild( header_key );
    this.header_key = header_key;

    const chest_x = FirstStepsOverlay.px( FirstStepsOverlay.CHEST_X );
    const chest_y = FirstStepsOverlay.px( FirstStepsOverlay.CHEST_Y );
    const chest_w = FirstStepsOverlay.px( FirstStepsOverlay.CHEST_W );
    const chest_h = FirstStepsOverlay.px( FirstStepsOverlay.CHEST_H );
    const claimable = state.claimable;

    const chest = new UIImage( this.ctx, chest_x, chest_y, chest_w, chest_h, ImgID.UI_FirstStepsChestClosed, {}, true );
    header.addChild( chest );

    if ( !claimable )
    {
      chest.setOnClick( () => this.showRewardsTooltip( chest ) );
      return;
    }

    const normal = new Vector2( 1, 1 );
    const pulse = new Vector2( FirstStepsOverlay.CHEST_PULSE_SCALE, FirstStepsOverlay.CHEST_PULSE_SCALE );
    chest.addTransform( new TransformScale( chest, true, normal, pulse, performance.now(), FirstStepsOverlay.CHEST_PULSE_MS, { easing: Easing.EaseInOutQuad, ping_pong_behaviour: true, infinite_loop: true } ) );
    chest.setOnClick( () => this.openChest() );
  }

  private showRewardsTooltip( chest: UIImage ): void
  {
    const rewards = this.state.rewards;
    if ( rewards.length === 0 || TooltipManager.isAnyTooltipOpened() )
      return;

    const content = rewards.map( reward => ( {
      img_id: ADVENTURE_REWARD_ICONS[reward.type],
      count: reward.amount,
      amount_text: reward.type === "coin" ? String( reward.amount ) : rewardAmountText( reward.type, reward.amount ),
    } ) );
    const cols = rewards.length <= FirstStepsOverlay.TOOLTIP_ONE_ROW_MAX ? rewards.length : Math.ceil( rewards.length / 2 );
    const { width, height } = AdventureTooltip.computeSize( rewards.length, cols );

    const chest_pos = chest.getAbsolutePosition();
    const anchor_x = chest_pos.x + chest.getRect().getW() / 2;
    const margin = LayoutScaler.widthFromDesign( FirstStepsOverlay.TOOLTIP_SCREEN_MARGIN );
    const tail_margin = LayoutScaler.widthFromDesign( FirstStepsOverlay.TOOLTIP_TAIL_MARGIN );
    const x = Math.max( margin, Math.min( Game.screenW() - width - margin, anchor_x - width + tail_margin ) );
    const y = Math.max( margin, chest_pos.y - height + LayoutScaler.heightFromDesign( FirstStepsOverlay.TOOLTIP_OVERLAP ) );
    const tail_x = Math.max( tail_margin, Math.min( width - tail_margin, anchor_x - x ) );

    TooltipManager.openTooltip( new AdventureTooltip( TooltipManager.ctx, x, y, width, height, tail_x, content, cols ), false, true );
  }

  private openChest(): void
  {
    const preview_rewards = this.overlay_params.preview ? this.state.rewards : undefined;
    const params: FirstStepsChestOverlayParams = {
      backdrop: this,
      preview_rewards,
      on_claimed: () => setTimeout( () =>
      {
        if ( OverlayManager.isOverlayOpen( this.getId() ) )
          OverlayManager.closeOverlay();
        this.overlay_params.on_claimed?.();
      }, 0 ),
    };

    const chest_overlay = new FirstStepsChestOverlay( OverlayManager.ctx, Game.safeAreaLeft(), Game.safeAreaTop(), Game.getContentWidth(), Game.getContentHeight(), params, OverlayId.FIRST_STEPS_CHEST_OVERLAY );
    OverlayManager.openOverlay( chest_overlay );
  }

  private createBar( parent: UIElement, skin: BarSkin, x: number, y: number, w: number, h: number, current: number, target: number, part_of_parent = true ): UIProgressBar
  {
    const inset_x = FirstStepsOverlay.px( skin.inset_x );
    const inset_y = FirstStepsOverlay.px( skin.inset_y );
    const fill_h = h - inset_y * 2;
    const progress = target > 0 ? Math.min( 1, current / target ) : 0;
    const track_cap = Math.round( h * skin.track_cap_ratio );
    const fill_cap = Math.round( fill_h * skin.fill_cap_ratio );
    const base_corner_sizes = {
      top_left: { width: track_cap, height: 0 },
      bottom_right: { width: track_cap, height: 0 },
    };
    const fill_corner_sizes = {
      top_left: { width: fill_cap, height: 0 },
      bottom_right: { width: fill_cap, height: 0 },
    };

    const bar = new UIProgressBar( this.ctx, x, y, w, h, {
      base_img_id: skin.track_id,
      fill_img_id: skin.fill_id,
      base_nine_slice: skin.track_key,
      fill_nine_slice: skin.fill_key,
      border_width: 0,
      fill_height: fill_h,
      fill_offset_left: inset_x,
      fill_offset_right: inset_x,
      base_corner_sizes,
      fill_corner_sizes,
      fill_corner_size: fill_cap,
      initial_progress: progress,
    } );
    bar.setClickable( false );
    parent.addChild( bar, part_of_parent );
    return bar;
  }

  private createPanel(): void
  {
    const x = FirstStepsOverlay.px( FirstStepsOverlay.PANEL_X );
    const y = FirstStepsOverlay.px( FirstStepsOverlay.PANEL_Y );
    const w = FirstStepsOverlay.px( FirstStepsOverlay.PANEL_W );
    const h = FirstStepsOverlay.px( FirstStepsOverlay.PANEL_H );

    const panel = new UIMirrorElement( this.ctx, x, y, w, h, MirrorMode.horizontal );
    panel.setImageSource( ImgID.UI_FirstStepsPanelHalf );
    this.content.addChild( panel );
    this.panel = panel;

    this.task_rows.clear();
    this.placed_done = new Set( this.state.tasks.filter( task => FirstStepsOverlay.isTaskDone( task ) && !this.fresh_ids.has( task.id ) ).map( task => task.id ) );

    this.orderedTasks().forEach( ( task, index ) =>
    {
      if ( !this.fresh_ids.has( task.id ) )
      {
        const row = this.createRow( panel, task, index, FirstStepsOverlay.isTaskDone( task ) );
        this.task_rows.set( task.id, [ row ] );
        return;
      }

      const before = this.createRow( panel, task, index, false );
      const after = this.createRow( panel, task, index, true );
      after.setOpacityImmediate( 0 );
      after.setClickable( false );
      this.task_rows.set( task.id, [ before, after ] );
      this.playTaskDone( panel, task, before, after );
    } );
  }

  private static isTaskDone( task: FirstStepsTaskDto ): boolean
  {
    return task.current >= task.target;
  }

  private orderedTasks(): FirstStepsTaskDto[]
  {
    const tasks = this.state.tasks;
    const active = tasks.filter( task => !this.placed_done.has( task.id ) );
    const done = tasks.filter( task => this.placed_done.has( task.id ) );
    return [ ...active, ...done ];
  }

  private static rowY( index: number ): number
  {
    return FirstStepsOverlay.px( FirstStepsOverlay.ROW_Y + FirstStepsOverlay.ROW_STEP * index );
  }

  private moveRowsToOrder(): void
  {
    const now = performance.now();
    this.orderedTasks().forEach( ( task, index ) =>
    {
      const y = FirstStepsOverlay.rowY( index );
      this.task_rows.get( task.id )?.forEach( row =>
      {
        const from = row.pixelPosImportant().clone();
        if ( from.y === y )
          return;

        const to = new Vector2( from.x, y );
        row.addTransform( new TransformStraightMove( row, true, from, to, now, FirstStepsOverlay.ROW_MOVE_MS, { easing: Easing.EaseInOutQuad } ) );
      } );
    } );
  }

  private playTaskDone( panel: UIElement, task: FirstStepsTaskDto, before: UIElement, after: UIElement ): void
  {
    const delay = FirstStepsOverlay.DONE_ANIM_DELAY_MS + FirstStepsOverlay.DONE_ANIM_STEP_MS * this.fresh_order++;
    const start = performance.now() + delay;
    const normal = new Vector2( 1, 1 );
    const pop = new Vector2( FirstStepsOverlay.ROW_POP_SCALE, FirstStepsOverlay.ROW_POP_SCALE );

    before.fadeToOpacity( 0, FirstStepsOverlay.ROW_SWAP_MS, start, Easing.EaseOutQuad );
    after.fadeToOpacity( 1, FirstStepsOverlay.ROW_SWAP_MS, start, Easing.EaseOutQuad );
    after.addTransform( new TransformScale( after, true, normal, pop, start, FirstStepsOverlay.ROW_SWAP_MS * 2, { easing: Easing.EaseInOutQuad, ping_pong_behaviour: true } ) );

    const build = this.build;
    setTimeout( () =>
    {
      before.setClickable( false );
      if ( build === this.build )
        this.flyKey( after );
    }, delay );
    setTimeout( () =>
    {
      panel.removeChild( before );
      after.setClickable( true );
      if ( build !== this.build )
        return;

      this.task_rows.set( task.id, [ after ] );
      this.placed_done.add( task.id );
      panel.removeChild( after );
      panel.addChild( after );
      this.moveRowsToOrder();
    }, delay + FirstStepsOverlay.ROW_SWAP_MS );
  }

  private flyKey( row: UIElement ): void
  {
    const bar = this.progress_bar;
    if ( !bar )
      return;

    this.shown_done += 1;
    const done = this.shown_done;
    const total = this.state.tasks.length;

    const size = FirstStepsOverlay.px( FirstStepsOverlay.ROW_KEY_SIZE );
    const content_pos = this.content.getAbsolutePosition();
    const row_pos = row.getAbsolutePosition();
    const from_x = row_pos.x - content_pos.x + FirstStepsOverlay.px( FirstStepsOverlay.ICON_CENTER_X ) - size / 2;
    const from_y = row_pos.y - content_pos.y + FirstStepsOverlay.px( FirstStepsOverlay.ICON_CENTER_Y ) - size / 2;

    const bar_pos = bar.getAbsolutePosition();
    const inset = FirstStepsOverlay.px( FirstStepsOverlay.PROGRESS_INSET_X );
    const fill_end = inset + ( bar.getRect().getW() - inset * 2 ) * Math.min( 1, done / total );
    const to_x = bar_pos.x - content_pos.x + fill_end - size / 2;
    const to_y = bar_pos.y - content_pos.y + bar.getRect().getH() / 2 - size / 2;

    const from = new Vector2( from_x, from_y );
    const to = new Vector2( to_x, to_y );
    const end_scale = new Vector2( FirstStepsOverlay.KEY_FLY_END_SCALE, FirstStepsOverlay.KEY_FLY_END_SCALE );
    const now = performance.now();

    const key = new UIImage( this.ctx, from_x, from_y, size, size, ImgID.UI_FirstStepsKey );
    key.setClickable( false );
    this.content.addChild( key );

    const move = new TransformStraightMove( key, true, from, to, now, FirstStepsOverlay.KEY_FLY_MS, { easing: Easing.EaseInOutQuad } );
    const build = this.build;
    move.setOnFinished( () => setTimeout( () =>
    {
      this.content.removeChild( key );
      if ( build === this.build )
        this.onKeyArrived( done, total );
    }, 0 ) );
    key.addTransform( move );
    key.addTransform( new TransformScale( key, true, new Vector2( 1, 1 ), end_scale, now, FirstStepsOverlay.KEY_FLY_MS, { easing: Easing.EaseInQuad } ) );
  }

  private onKeyArrived( done: number, total: number ): void
  {
    this.progress_bar?.animateToProgress( Math.min( 1, done / total ), FirstStepsOverlay.PROGRESS_FILL_MS );
    this.progress_text?.setText( `${done}/${total}` );

    if ( !this.header_key )
      return;

    const normal = new Vector2( 1, 1 );
    const pop = new Vector2( FirstStepsOverlay.HEADER_KEY_POP_SCALE, FirstStepsOverlay.HEADER_KEY_POP_SCALE );
    this.header_key.addTransform( new TransformScale( this.header_key, true, normal, pop, performance.now(), FirstStepsOverlay.HEADER_KEY_POP_MS, { easing: Easing.EaseInOutQuad, ping_pong_behaviour: true } ) );
  }

  private createRow( panel: UIElement, task: FirstStepsTaskDto, index: number, done: boolean ): UIElement
  {
    const x = FirstStepsOverlay.px( FirstStepsOverlay.ROW_X );
    const y = FirstStepsOverlay.rowY( index );
    const w = FirstStepsOverlay.px( FirstStepsOverlay.ROW_W );
    const h = FirstStepsOverlay.px( FirstStepsOverlay.ROW_H );
    const cap_scale = h / FirstStepsOverlay.ROW_SRC_H;
    const left_cap = FirstStepsOverlay.ROW_SRC_LEFT_CAP * cap_scale;
    const right_cap = FirstStepsOverlay.ROW_SRC_RIGHT_CAP * cap_scale;
    const bg_id = done ? ImgID.UI_FirstStepsTaskBgDone : ImgID.UI_FirstStepsTaskBg;
    const bg_key = done ? "first_steps_task_bg_done" : "first_steps_task_bg";

    const row = this.addNineSlice( panel, bg_id, bg_key, x, y, w, h, left_cap, right_cap, false );

    this.createRowIcon( row, done );
    this.createRowTitle( row, FirstStepsOverlay.taskTitle( task ) );

    if ( done )
    {
      this.createRowDone( row );
      return row;
    }

    this.createRowBar( row, task );
    this.createRowButton( row, task );
    return row;
  }

  private createRowIcon( row: UIElement, done: boolean ): void
  {
    const id = done ? ImgID.UI_FirstStepsCheck : ImgID.UI_FirstStepsKey;
    const w = FirstStepsOverlay.px( done ? FirstStepsOverlay.CHECK_W : FirstStepsOverlay.ROW_KEY_SIZE );
    const h = FirstStepsOverlay.px( done ? FirstStepsOverlay.CHECK_H : FirstStepsOverlay.ROW_KEY_SIZE );
    const x = FirstStepsOverlay.px( FirstStepsOverlay.ICON_CENTER_X ) - w / 2;
    const y = FirstStepsOverlay.px( FirstStepsOverlay.ICON_CENTER_Y ) - h / 2;

    this.addImage( row, id, x, y, w, h );
  }

  private createRowTitle( row: UIElement, title: string ): void
  {
    const x = FirstStepsOverlay.px( FirstStepsOverlay.TITLE_X );
    const y = FirstStepsOverlay.px( FirstStepsOverlay.TITLE_Y );
    const w = FirstStepsOverlay.px( FirstStepsOverlay.TITLE_W );
    const h = FirstStepsOverlay.px( FirstStepsOverlay.TITLE_H );

    this.addText( row, x, y, w, h, {
      text: title,
      font_size: LayoutScaler.textSizeFromDesign( FirstStepsOverlay.TITLE_FONT ),
      min_font_size: LayoutScaler.textSizeFromDesign( FirstStepsOverlay.TITLE_MIN_FONT ),
      text_color: ColorScheme.first_steps_task_title,
      text_align: MyTextAlign.Left,
      scale_to_width: true,
    } );
  }

  private createRowBar( row: UIElement, task: FirstStepsTaskDto ): void
  {
    const x = FirstStepsOverlay.px( FirstStepsOverlay.BAR_X );
    const y = FirstStepsOverlay.px( FirstStepsOverlay.BAR_Y );
    const w = FirstStepsOverlay.px( FirstStepsOverlay.BAR_W );
    const h = FirstStepsOverlay.px( FirstStepsOverlay.BAR_H );

    const bar = this.createBar( row, FirstStepsOverlay.TASK_SKIN, x, y, w, h, task.current, task.target );

    this.addText( bar, 0, 0, w, h, {
      text: `${Math.min( task.current, task.target )}/${task.target}`,
      font_size: LayoutScaler.textSizeFromDesign( FirstStepsOverlay.BAR_FONT ),
      outline_color: ColorScheme.first_steps_value_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( FirstStepsOverlay.TEXT_OUTLINE ),
    } );
  }

  private createRowButton( row: UIElement, task: FirstStepsTaskDto ): void
  {
    const x = FirstStepsOverlay.px( FirstStepsOverlay.BUTTON_X );
    const y = FirstStepsOverlay.px( FirstStepsOverlay.BUTTON_Y );
    const w = FirstStepsOverlay.px( FirstStepsOverlay.BUTTON_W );
    const h = FirstStepsOverlay.px( FirstStepsOverlay.BUTTON_H );

    const button = new UIImage( this.ctx, x, y, w, h, ImgID.UI_FirstStepsButton, {}, true );
    row.addChild( button );

    this.addText( button, 0, 0, w, h, {
      localization_key: "FIRST_STEPS.GO",
      font_size: LayoutScaler.textSizeFromDesign( FirstStepsOverlay.BUTTON_FONT ),
      outline_color: ColorScheme.first_steps_button_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( FirstStepsOverlay.TEXT_OUTLINE ),
      scale_to_width: true,
      shadow_blur: LayoutScaler.widthFromDesign( 6 ),
      shadow_offset_x: 0,
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      shadow_color: ColorScheme.black_transparent_30
    } );

    button.setOnClick( () =>
    {
      OverlayManager.closeOverlay();
      this.overlay_params.on_go?.( task );
    } );
  }

  private createRowDone( row: UIElement ): void
  {
    const x = FirstStepsOverlay.px( FirstStepsOverlay.DONE_X );
    const y = FirstStepsOverlay.px( FirstStepsOverlay.DONE_Y );
    const w = FirstStepsOverlay.px( FirstStepsOverlay.DONE_W );
    const h = FirstStepsOverlay.px( FirstStepsOverlay.DONE_H );

    this.addText( row, x, y, w, h, {
      localization_key: "FIRST_STEPS.DONE",
      font_size: LayoutScaler.textSizeFromDesign( FirstStepsOverlay.DONE_FONT ),
      text_color: ColorScheme.white,
      text_align: MyTextAlign.Left,
      outline_color: ColorScheme.first_steps_task_title,
      outline_width: LayoutScaler.textBorderSizeFromDesign( FirstStepsOverlay.TEXT_OUTLINE ),
    } );
  }

  private createCloseButton(): void
  {
    const size = FirstStepsOverlay.px( FirstStepsOverlay.CLOSE_SIZE );
    const x = FirstStepsOverlay.px( FirstStepsOverlay.CLOSE_X );
    const y = FirstStepsOverlay.px( FirstStepsOverlay.CLOSE_Y );

    const close = new UIImage( this.ctx, x, y, size, size, ImgID.UI_SettingsDialogCloseBtn, {}, true );
    close.setOnClick( () => OverlayManager.closeOverlay() );
    this.content.addChild( close );
  }

  public override onOverlayWillClose(): void
  {
    if ( TooltipManager.isAnyTooltipOpened() )
      TooltipManager.closeTooltip();
  }

  public getDimOpacity(): number
  {
    return 0.8;
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "FirstStepsOverlay";
  }
}
